import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { StepSection } from '../index';
import {
  ONBOARDING_STEPS,
  ONBOARDING_STEP_CREDIT_SCORE_KEY,
  ONBOARDING_STEP_PROPERTY_KIND_KEY,
  ONBOARDING_STEP_INVESTMENT_EXPERIENCE_KEY,
  ONBOARDING_STEP_PROPERTY_CONDITION_KEY,
  ONBOARDING_STEP_LOAN_PURPOSE_KEY,
  getDisabledStateCodes,
} from '../../../../utils/constants';
import { FormValues, Ownership } from '../../../../types';
import { trackEvent, QuestionAnsweredFromPage, trackSectionLoadedEvent } from '../../../../utils/mixpanel';
import { Button } from '../../../borrower/components';
import { useOwnership } from '../../../onboarding/context/OwnershipContext';
import { InputAutocompleteAddress, TextInput } from '../../../../shared/components/ui/atoms/TextInput/TextInput';
import { useAuth } from '../../../auth/context/AuthContext';
import { formatAddressWithoutCountry } from '../../../../utils/functions';

const PROPERTY_LOCATION_KEY = 'Where is the property located?';
const DEFAULT_AUTO_LOCATION = 'Alabama';

const buildFullName = (first?: string, last?: string): string | undefined => {
  const normalizedFirst = typeof first === 'string' ? first.trim() : '';
  const normalizedLast = typeof last === 'string' ? last.trim() : '';
  const parts = [normalizedFirst, normalizedLast].filter((part) => part.length > 0);
  return parts.length > 0 ? parts.join(' ') : undefined;
};

interface LoggedInQuestionaryContentProps {
  selectedIndex: number;
  setSelectedIndex: (index: number, options?: { force?: boolean; fromSelection?: boolean }) => void;
  values: FormValues;
  setValue: (key: string, value: string | number) => void;
  setBottomValue: (value: number) => void;
  isBrokerMode: boolean;
  ownerships: Ownership[];
  resumeStepIndex: number;
  children?: React.ReactNode;
}

export const LoggedInQuestionaryContent: React.FC<LoggedInQuestionaryContentProps> = (props) => {
  const {
    selectedIndex,
    setSelectedIndex,
    values,
    setValue,
    setBottomValue,
    isBrokerMode,
    ownerships,
    resumeStepIndex,
    children,
  } = props;

  const { setOwnershipSelected, ownershipSelected } = useOwnership();
  const { user } = useAuth();

  const [investmentAddress, setInvestmentAddress] = useState<string>(() => {
    const initialAddress = values['Investment property address'];
    return typeof initialAddress === 'string' ? initialAddress : '';
  });
  const [selectedInvestmentAddress, setSelectedInvestmentAddress] = useState<string>(() => {
    const initialAddress = values['Investment property address'];
    return typeof initialAddress === 'string' ? initialAddress : '';
  });
  const [investmentAddressFormatted, setInvestmentAddressFormatted] = useState<string>(() => {
    const initialAddress = values['Investment property address'];
    return typeof initialAddress === 'string' ? initialAddress : '';
  });
  const [guarantorFirstName, setGuarantorFirstName] = useState<string>('');
  const [guarantorLastName, setGuarantorLastName] = useState<string>('');
  const clearingSelectedAddressRef = useRef(false);
  const autoLocationAppliedRef = useRef(false);
  const autoAdvancedFromRepairsRef = useRef(false);
  const isSelectingAddressRef = useRef(false);

  const isSomeoneElse = useMemo(() => ownershipSelected?.isNew === true, [ownershipSelected]);
  const shouldSkipPropertyKindStep = false;
  const shouldSkipCreditScoreStep = useMemo(
    () => Boolean(ownershipSelected && ownershipSelected.isNew !== true),
    [ownershipSelected]
  );
  const shouldSkipInvestmentExperienceStep = useMemo(
    () => Boolean(ownershipSelected && ownershipSelected.isNew !== true),
    [ownershipSelected]
  );

  const storedGuarantorFirstName = values['guarantorFirstName'];
  const storedGuarantorLastName = values['guarantorLastName'];

  useEffect(() => {
    trackSectionLoadedEvent('Background Questionary (Logged In)');
  }, []);

  // Initialize investmentAddress from values if available
  useEffect(() => {
    if (isSelectingAddressRef.current) {
      isSelectingAddressRef.current = false;
      return;
    }
    const addressFromValues = values['Investment property address'];

    if (typeof addressFromValues === 'string' && addressFromValues.trim() !== '') {
      setSelectedInvestmentAddress(addressFromValues);
      setInvestmentAddress(addressFromValues);
      clearingSelectedAddressRef.current = false;
      return;
    }

    if (addressFromValues === '' || addressFromValues === undefined) {
      setSelectedInvestmentAddress('');
      if (!clearingSelectedAddressRef.current) {
        setInvestmentAddress('');
      }
      clearingSelectedAddressRef.current = false;
    }
  }, [values['Investment property address']]);

  const handleSelect = (key: string, value: string | number): void => {
    setValue(key, value);
    trackEvent(QuestionAnsweredFromPage(key, value));
  };

  const guarantorDescription = (
    <>
      If you have more than one guarantor on the loan, just choose one person. You&apos;ll add the other person later. We&apos;ll still send all loan information and updates to <strong>{user?.email ?? '[account email]'}</strong>
    </>
  );

  const guarantorStep = (
    <StepSection
      key={'__guarantor__'}
      step={{ iconPath: './assets/icons/icon-bulb.svg', text: 'Who will be a guarantor on this loan?', description: guarantorDescription, options: [] } as any}
      isSelected={selectedIndex === 0}
      value={undefined}
      onSelect={undefined}
      setBottomValue={setBottomValue}
      isBrokerMode={isBrokerMode}
      stepIndex={0}
      customMarkup={
        <>
          <div className='text-borrower'>
            {ownerships?.map((ownership) => (
              <div
                key={ownership._id}
                /* style={{ border: '1px solid #CCD3DB', background: '#F9FAFB', color: '#2A2F3C', padding: '18px 24px', cursor: 'pointer', marginBottom: '24px', fontWeight: '600' }} */
                className='select-option description'
                onClick={() => {
                  setOwnershipSelected(ownership);
                  applyOwnershipDefaults(ownership);
                  setSelectedIndex(1, { force: true });
                }}
              >
                <span style={{ textTransform: 'capitalize', fontWeight: 'bold' }}>{ownership.name} {ownership.lastname}</span>
              </div>
            ))}
            <div
              className='select-option description'
              onClick={() => {
                setOwnershipSelected({ _id: '', email: '', state: '', name: 'Someone', lastname: 'else', isNew: true } as Ownership);
                if (values[propertyKindStepText]) {
                  setValue(propertyKindStepText, '');
                }
                if (values[creditScoreStepText]) {
                  setValue(creditScoreStepText, '');
                }
                if (values[investmentExperienceStepText]) {
                  setValue(investmentExperienceStepText, '');
                }
                if (values.propertyType) {
                  setValue('propertyType', '');
                }
                setSelectedIndex(1, { force: true });
              }}
            >
              <strong>Someone else</strong>
              <span style={{ fontSize: '16px', color: '#404859', display: 'block', fontWeight: '400' }}>Choose this option if someone else is the guarantor on the loan</span>
            </div>
          </div>
        </>
      }
    />
  );

  const addressBaseIndex = 1;
  const locationStepIndex = useMemo(
    () => ONBOARDING_STEPS.findIndex((s) => s.text === 'Where is the property located?'),
    []
  );
  const propertyKindStepIndex = useMemo(
    () => ONBOARDING_STEPS.findIndex((s) => s.text === 'What kind of property is it?'),
    []
  );
  const creditScoreStepIndex = useMemo(
    () => ONBOARDING_STEPS.findIndex((s) => s.shortKey === ONBOARDING_STEP_CREDIT_SCORE_KEY),
    []
  );
  const investmentExperienceStepIndex = useMemo(
    () => ONBOARDING_STEPS.findIndex((s) => s.shortKey === ONBOARDING_STEP_INVESTMENT_EXPERIENCE_KEY),
    []
  );
  const propertyConditionStepIndex = useMemo(
    () => ONBOARDING_STEPS.findIndex((s) => s.shortKey === ONBOARDING_STEP_PROPERTY_CONDITION_KEY),
    []
  );
  const loanPurposeStepIndex = useMemo(
    () => ONBOARDING_STEPS.findIndex((s) => s.shortKey === ONBOARDING_STEP_LOAN_PURPOSE_KEY),
    []
  );
  const propertyKindStepText = useMemo(() => {
    const propertyKindStep = ONBOARDING_STEPS.find(
      (step) => step.shortKey === ONBOARDING_STEP_PROPERTY_KIND_KEY
    );
    return propertyKindStep?.text ?? 'What kind of property is it?';
  }, []);
  const creditScoreStepText = useMemo(() => {
    const creditScoreStep = ONBOARDING_STEPS.find(
      (step) => step.shortKey === ONBOARDING_STEP_CREDIT_SCORE_KEY
    );
    return creditScoreStep?.text ?? "What’s your credit score?";
  }, []);
  const investmentExperienceStepText = useMemo(() => {
    const investmentExperienceStep = ONBOARDING_STEPS.find(
      (step) => step.shortKey === ONBOARDING_STEP_INVESTMENT_EXPERIENCE_KEY
    );
    return investmentExperienceStep?.text ?? 'How many properties have you purchased/sold in the last 24 months?';
  }, []);
  const shouldSkipLocationStep = Boolean(selectedInvestmentAddress);

  const getGuarantorName = useCallback(() => {
    const storedGuarantorName = buildFullName(
      typeof storedGuarantorFirstName === 'string' ? storedGuarantorFirstName : undefined,
      typeof storedGuarantorLastName === 'string' ? storedGuarantorLastName : undefined
    );

    const draftGuarantorName = buildFullName(guarantorFirstName, guarantorLastName);

    const ownershipGuarantorName =
      ownershipSelected && ownershipSelected.isNew !== true
        ? buildFullName(
            typeof ownershipSelected?.name === 'string' ? ownershipSelected.name : undefined,
            typeof ownershipSelected?.lastname === 'string' ? ownershipSelected.lastname : undefined
          ) ||
          buildFullName(
            typeof (ownershipSelected as any)?.firstName === 'string'
              ? (ownershipSelected as any).firstName
              : undefined,
            typeof (ownershipSelected as any)?.lastName === 'string'
              ? (ownershipSelected as any).lastName
              : undefined
          )
        : undefined;

    return storedGuarantorName || draftGuarantorName || ownershipGuarantorName;
  }, [
    guarantorFirstName,
    guarantorLastName,
    ownershipSelected,
    storedGuarantorFirstName,
    storedGuarantorLastName,
  ]);

  const creditScoreDisplayText = useMemo(() => {
    const resolvedName = getGuarantorName();

    if (!resolvedName) {
      return undefined;
    }

    return `What's ${resolvedName}'s estimated credit score?`;
  }, [getGuarantorName]);

  const investmentExperienceDisplayText = useMemo(() => {
    const resolvedName = getGuarantorName();

    if (!resolvedName) {
      return undefined;
    }

    return `How many properties has ${resolvedName} purchased/sold in the last 36 months?`;
  }, [getGuarantorName]);

  const computeFlowIndex = useCallback((stepListIndex: number): number => {
    let baseIndex = addressBaseIndex + 1 + stepListIndex;
    if (shouldSkipLocationStep && locationStepIndex !== -1 && stepListIndex > locationStepIndex) {
      baseIndex -= 1;
    }
    if (shouldSkipPropertyKindStep && propertyKindStepIndex !== -1 && stepListIndex > propertyKindStepIndex) {
      baseIndex -= 1;
    }
    if (shouldSkipCreditScoreStep && creditScoreStepIndex !== -1 && stepListIndex > creditScoreStepIndex) {
      baseIndex -= 1;
    }
    if (shouldSkipInvestmentExperienceStep && investmentExperienceStepIndex !== -1 && stepListIndex > investmentExperienceStepIndex) {
      baseIndex -= 1;
    }
    return baseIndex;
  }, [
    addressBaseIndex,
    creditScoreStepIndex,
    investmentExperienceStepIndex,
    locationStepIndex,
    propertyKindStepIndex,
    shouldSkipCreditScoreStep,
    shouldSkipInvestmentExperienceStep,
    shouldSkipLocationStep,
    shouldSkipPropertyKindStep,
  ]);

  // Calculate the index for guarantorDetailsStep - it should appear after "What kind of property is it?"
  // "What kind of property is it?" is at index 4 in ONBOARDING_STEPS
  const guarantorDetailsStepIndex = useMemo(() => {
    if (propertyKindStepIndex === -1) {
      return addressBaseIndex + 1;
    }
    return computeFlowIndex(propertyKindStepIndex) + 1;
  }, [computeFlowIndex, propertyKindStepIndex]);

  const guarantorDetailsStep = (
    <StepSection
      key={'__guarantor_details__'}
      step={{ iconPath: './assets/icons/icon-contract.svg', text: "What's the guarantor's name?", description: '', options: [] } as any}
      isSelected={selectedIndex === guarantorDetailsStepIndex && isSomeoneElse}
      value={undefined}
      onSelect={undefined}
      setBottomValue={setBottomValue}
      isBrokerMode={isBrokerMode}
      stepIndex={guarantorDetailsStepIndex}
      customMarkup={
        <div className='guarantor-details'>
          <div className='input-container'>
            <TextInput className={'guarantor-input'} label={'First name'} value={guarantorFirstName} onChange={(v: string) => setGuarantorFirstName(v)} />
            <TextInput className={'guarantor-input'} label={'Last name'} value={guarantorLastName} onChange={(v: string) => setGuarantorLastName(v)} />
          </div>
          <div className='step-actions' style={{ marginTop: 16 }}>
            <Button
              outlined={false}
              isLoading={false}
              disabled={!guarantorFirstName || !guarantorLastName}
              onClick={() => {
                setValue('guarantorFirstName', guarantorFirstName);
                setValue('guarantorLastName', guarantorLastName);
                setSelectedIndex(guarantorDetailsStepIndex + 1, { fromSelection: true });
              }}
            >
              {guarantorFirstName && guarantorLastName ? 'Continue' : 'Enter name to continue'}
            </Button>
          </div>
        </div>
      }
    />
  );

  const stepsAfterAddress = useMemo(() => ONBOARDING_STEPS, []);
  const locationValue = values[PROPERTY_LOCATION_KEY];

  const addressStep = (
    <StepSection
      key={'__address__'}
      step={{ iconPath: './assets/icons/icon-map.svg', text: "What's the property's address?", description: 'Entering an exact address can help us give you a more accurate rate', options: [] } as any}
      isSelected={selectedIndex === addressBaseIndex}
      value={selectedInvestmentAddress || undefined}
      onSelect={undefined}
      setBottomValue={setBottomValue}
      isBrokerMode={isBrokerMode}
      stepIndex={addressBaseIndex}
      customMarkup={
        <div>
          <div className='allow-select-search'>
            <TextInput label={'Investment property address'} value={investmentAddress}>
              <InputAutocompleteAddress
                /* useNative */
                value={investmentAddress}
                onChange={(address: string) => {
                  setInvestmentAddress(address);
                  if (selectedInvestmentAddress) {
                    clearingSelectedAddressRef.current = true;
                    setSelectedInvestmentAddress('');
                    setValue('Investment property address', '');
                    setValue('Investment property address formatted', '');
                  } else if (!address) {
                    setValue('Investment property address', '');
                    setValue('Investment property address formatted', '');
                  }
                }}
                onSelect={(data: any) => {
                  isSelectingAddressRef.current = true;
                  const selectedAddress = data.selectedAddress || data.formatted_address;
                  const displayAddress = formatAddressWithoutCountry(selectedAddress);
                  setInvestmentAddress(displayAddress);

                  setSelectedInvestmentAddress(data.formatted_address);
                  setInvestmentAddressFormatted(formatAddressWithoutCountry(data.formatted_address));

                  clearingSelectedAddressRef.current = false;
                  setValue('Investment property address', displayAddress); // Guardar el completo
                  setValue('Investment property address formatted', formatAddressWithoutCountry(data.formatted_address));
                  trackEvent(QuestionAnsweredFromPage('Investment property address', data.formatted_address));
                }}
                excludedStates={getDisabledStateCodes()}
              />
            </TextInput>
          </div>
          <div className='step-actions' style={{ marginTop: 16 }}>
            <Button
              outlined={false}
              isLoading={false}
              disabled={!selectedInvestmentAddress}
              onClick={() => {
                if (selectedInvestmentAddress) {
                  setValue('__progress_after_address__', 1);
                  setSelectedIndex(addressBaseIndex + 1, { fromSelection: true });
                }
              }}
            >
              {selectedInvestmentAddress ? 'Continue' : 'Select an address to continue'}
            </Button>
            <div className='step-actions-separator'></div>
            <div className='address-alt-button'>
              <Button
                outlined={true}
                isLoading={false}
                disabled={false}
                onClick={() => {
                  setInvestmentAddress('');
                  setSelectedInvestmentAddress('');
                  setValue('__progress_after_address__', 1);
                  setValue('Investment property address', '');
                  setValue('Investment property address formatted', '');
                  setSelectedIndex(addressBaseIndex + 1, { fromSelection: true });
                }}
              >
                I don&apos;t have a specific property in mind
              </Button>
            </div>
          </div>
        </div>
      }
    />
  );

  useEffect(() => {
    if (shouldSkipLocationStep) {
      if (locationValue !== DEFAULT_AUTO_LOCATION) {
        setValue(PROPERTY_LOCATION_KEY, DEFAULT_AUTO_LOCATION);
        trackEvent(QuestionAnsweredFromPage(PROPERTY_LOCATION_KEY, DEFAULT_AUTO_LOCATION));
      }
      autoLocationAppliedRef.current = true;
    } else if (autoLocationAppliedRef.current) {
      if (locationValue === DEFAULT_AUTO_LOCATION) {
        setValue(PROPERTY_LOCATION_KEY, '');
      }
      autoLocationAppliedRef.current = false;
    }
  }, [locationValue, setValue, shouldSkipLocationStep]);

  // Check if we're currently on the property kind step
  const propertyKindStepIndexInFlow = propertyKindStepIndex === -1 || shouldSkipPropertyKindStep
    ? -1
    : computeFlowIndex(propertyKindStepIndex);
  const isOnPropertyKindStep = selectedIndex === propertyKindStepIndexInFlow;

  // Render guarantorDetailsStep standalone if:
  // 1. isSomeoneElse is true
  // 2. We're on the guarantorDetailsStepIndex
  // 3. We're NOT on the property kind step (because it will be rendered in the map in that case)
  const shouldShowGuarantorStepStandalone = isSomeoneElse &&
    selectedIndex === guarantorDetailsStepIndex &&
    !isOnPropertyKindStep;

  const applyOwnershipDefaults = useCallback(
    (ownership: Ownership | null | undefined) => {
      if (!ownership || ownership.isNew) {
        return;
      }

      const lastLoanData =
        ownership.loans?.[ownership.loans.length - 1]?.loanData ?? ownership.loanData;
      if (!lastLoanData) {
        return;
      }

      const creditScoreCandidates = [
        lastLoanData?.["What’s your credit score?"],
        lastLoanData?.["What's your credit score?"],
        lastLoanData?.creditScore,
        lastLoanData?.credit_score,
        lastLoanData?.fico,
        lastLoanData?.fico_range,
      ];
      const creditScoreValue = creditScoreCandidates.find((candidate) => {
        if (typeof candidate === 'string') {
          return candidate.trim().length > 0;
        }
        if (typeof candidate === 'number') {
          return !Number.isNaN(candidate);
        }
        return false;
      });

      if (
        creditScoreValue !== undefined &&
        creditScoreValue !== null &&
        (!values[creditScoreStepText] || String(values[creditScoreStepText]).trim().length === 0)
      ) {
        const normalizedCreditScoreValue = String(creditScoreValue).trim();
        if (values[creditScoreStepText] !== normalizedCreditScoreValue) {
          setValue(creditScoreStepText, normalizedCreditScoreValue);
        }
      }

      const investmentExperienceCandidates = [
        lastLoanData?.[investmentExperienceStepText],
        lastLoanData?.['How many properties have you purchased/sold in the last 24 months?'],
        lastLoanData?.['How many properties have you purchased/sold in the last 36 months?'],
        lastLoanData?.propertiesNegotiated,
        lastLoanData?.properties_negotiated,
        lastLoanData?.propertiesPurchased,
        lastLoanData?.properties_purchased,
      ];
      const investmentExperienceValue = investmentExperienceCandidates.find((candidate) => {
        if (typeof candidate === 'string') {
          return candidate.trim().length > 0;
        }
        if (typeof candidate === 'number') {
          return !Number.isNaN(candidate);
        }
        return false;
      });

      if (
        investmentExperienceValue !== undefined &&
        investmentExperienceValue !== null &&
        (!values[investmentExperienceStepText] ||
          String(values[investmentExperienceStepText]).trim().length === 0)
      ) {
        const normalizedInvestmentExperienceValue = String(investmentExperienceValue).trim();
        if (values[investmentExperienceStepText] !== normalizedInvestmentExperienceValue) {
          setValue(investmentExperienceStepText, normalizedInvestmentExperienceValue);
        }
      }

      const propertyTypeValue =
        (typeof lastLoanData?.propertyType === 'string' &&
        lastLoanData?.propertyType.trim().length > 0
          ? lastLoanData.propertyType
          : undefined) ||
        (typeof lastLoanData?.property_type === 'string' &&
        lastLoanData?.property_type.trim().length > 0
          ? lastLoanData.property_type
          : undefined);

      if (propertyTypeValue && values.propertyType !== propertyTypeValue) {
        setValue('propertyType', propertyTypeValue);
      }
    },
    [creditScoreStepText, investmentExperienceStepText, propertyKindStepText, setValue, values]
  );

  useEffect(() => {
    applyOwnershipDefaults(ownershipSelected ?? null);
  }, [applyOwnershipDefaults, ownershipSelected]);

  // Auto-advance when "It needs some repairs and/or construction" is selected
  // This skips the loan length step and goes directly to "Do you currently own this property?"
  useEffect(() => {
    const propertyConditionValue = values["What condition is the property in?"];
    const isRepairsSelected = propertyConditionValue === "It needs some repairs and/or construction";
    
    // Reset the ref when repairs option is not selected
    if (!isRepairsSelected) {
      autoAdvancedFromRepairsRef.current = false;
      return;
    }
    
    // Only auto-advance if we haven't already done so and loan purpose step exists
    if (isRepairsSelected && loanPurposeStepIndex !== -1 && !autoAdvancedFromRepairsRef.current) {
      const propertyConditionFlowIndex = computeFlowIndex(propertyConditionStepIndex);
      const loanPurposeFlowIndex = computeFlowIndex(loanPurposeStepIndex);
      
      // Check if we're on property condition step or the next step (loan length)
      // This handles the case where the selection just happened
      if (selectedIndex === propertyConditionFlowIndex || 
          selectedIndex === propertyConditionFlowIndex + 1) {
        autoAdvancedFromRepairsRef.current = true;
        // Use a small delay to ensure state is updated
        setTimeout(() => {
          setSelectedIndex(loanPurposeFlowIndex, { force: true, fromSelection: true });
        }, 150);
      }
    }
  }, [
    values["What condition is the property in?"],
    selectedIndex,
    propertyConditionStepIndex,
    loanPurposeStepIndex,
    computeFlowIndex,
    setSelectedIndex,
  ]);

  return (
    <React.Fragment>
      {guarantorStep}
      {addressStep}
      {stepsAfterAddress.map((step, i) => {
        // Calculate the actual index for this step
        // If isSomeoneElse is true and this step comes after "What kind of property is it?",
        // we need to add 1 to account for the guarantorDetailsStep that was inserted
        if (shouldSkipLocationStep && step.text === 'Where is the property located?') {
          return null;
        }
        if (shouldSkipPropertyKindStep && propertyKindStepIndex !== -1 && i >= propertyKindStepIndex) {
          return null;
        }
        if (shouldSkipCreditScoreStep && step.text === creditScoreStepText) {
          return null;
        }
        if (shouldSkipInvestmentExperienceStep && step.text === investmentExperienceStepText) {
          return null;
        }

        const baseFlowIndex = computeFlowIndex(i);
        const stepIndex = isSomeoneElse && propertyKindStepIndex !== -1 && i > propertyKindStepIndex
          ? baseFlowIndex + 1
          : baseFlowIndex;

        // Check if we need to insert guarantorDetailsStep after "What kind of property is it?"
        const shouldInsertGuarantorStep = isSomeoneElse &&
          propertyKindStepIndex !== -1 &&
          step.text === "What kind of property is it?" &&
          i === propertyKindStepIndex;

        const isCreditScoreStep = step.shortKey === ONBOARDING_STEP_CREDIT_SCORE_KEY;
        const isInvestmentExperienceStep = step.shortKey === ONBOARDING_STEP_INVESTMENT_EXPERIENCE_KEY;
        
        let stepDisplayText = step.displayText;
        if (isCreditScoreStep) {
          stepDisplayText = creditScoreDisplayText ?? step.displayText;
        } else if (isInvestmentExperienceStep) {
          stepDisplayText = investmentExperienceDisplayText ?? step.displayText;
        }
        
        const stepForRender =
          stepDisplayText === step.displayText ? step : { ...step, displayText: stepDisplayText };

        return (
          <React.Fragment key={step.text}>
            <StepSection
              key={step.text}
              step={stepForRender}
              isSelected={selectedIndex === stepIndex}
              value={values[step.text] ? String(values[step.text]) : undefined}
              stepIndex={stepIndex}
              onSelect={(value) => {
                // If this is "What condition is the property in?" and user selected "It needs some repairs and/or construction"
                // Skip the loan length step and go directly to "Do you currently own this property?"
                const isPropertyConditionStep = step.text === "What condition is the property in?";
                const isRepairsOption = value === "It needs some repairs and/or construction";
                
                if (isPropertyConditionStep && isRepairsOption && loanPurposeStepIndex !== -1) {
                  handleSelect(step.text, value);
                  // Calculate the target step index
                  const loanPurposeFlowIndex = computeFlowIndex(loanPurposeStepIndex);
                  // Use requestAnimationFrame to ensure DOM and state updates are complete
                  requestAnimationFrame(() => {
                    setTimeout(() => {
                      setSelectedIndex(loanPurposeFlowIndex, { force: true, fromSelection: true });
                    }, 0);
                  });
                  return;
                }
                
                handleSelect(step.text, value);
                // Do not auto-advance on state selection; require explicit Continue
                if (step.text !== 'Where is the property located?') {
                  
                  // If this is "What kind of property is it?" and isSomeoneElse, advance to guarantorDetailsStep
                  // Only navigate to guarantor step if guarantorFirstName is missing
                  if (shouldInsertGuarantorStep) {
                    const guarantorFirstName = values?.guarantorFirstName || '';
                    if (!guarantorFirstName) {
                      setSelectedIndex(guarantorDetailsStepIndex, { fromSelection: true });
                    } else {
                      // If guarantor name already exists, skip to next step
                      const nextStepIndex = guarantorDetailsStepIndex + 1;
                      setSelectedIndex(nextStepIndex, { fromSelection: true });
                    }
                  } else if (shouldSkipPropertyKindStep && propertyKindStepIndex !== -1 && i >= propertyKindStepIndex - 1) {
                    setSelectedIndex(resumeStepIndex, { fromSelection: true });
                  } else {
                    const hasNextRenderableStep = stepsAfterAddress.slice(i + 1).some((nextStep) => {
                      if (shouldSkipLocationStep && nextStep.text === 'Where is the property located?') {
                        return false;
                      }
                      if (shouldSkipPropertyKindStep && nextStep.text === propertyKindStepText) {
                        return false;
                      }
                      if (shouldSkipCreditScoreStep && nextStep.text === creditScoreStepText) {
                        return false;
                      }
                      if (shouldSkipInvestmentExperienceStep && nextStep.text === investmentExperienceStepText) {
                        return false;
                      }
                      return true;
                    });

                    if (!hasNextRenderableStep) {
                      setSelectedIndex(resumeStepIndex, { fromSelection: true });
                    } else {
                      setSelectedIndex(stepIndex + 1, { fromSelection: true });
                    }
                  }
                }
              }}
              setBottomValue={setBottomValue}
              isBrokerMode={isBrokerMode}
              customMarkup={
                step.text === 'Where is the property located?'
                  ? (
                    <div className='step-actions' style={{ marginTop: 16 }}>
                      <Button
                        outlined={false}
                        isLoading={false}
                        disabled={!values[step.text]}
                        onClick={() => setSelectedIndex(stepIndex + 1, { fromSelection: true })}
                      >
                        Continue
                      </Button>
                    </div>
                  )
                  : (i === 5) ? <div className="space"></div> : null
              }
            />
            {shouldInsertGuarantorStep && guarantorDetailsStep}
          </React.Fragment>
        );
      })}
      {/* Render guarantorDetailsStep if user navigates directly to it (e.g., going back) */}
      {shouldShowGuarantorStepStandalone && guarantorDetailsStep}
      {children}
    </React.Fragment>
  );
}
